import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./IdSettlement.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const LIMIT = 20;

const bankLine = (a) =>
  a && a.accountNumber
    ? `${a.bankName || "Bank"} · A/c ${String(a.accountNumber).replace(/.(?=.{4})/g, "•")}${a.ifscCode ? ` · ${a.ifscCode}` : ""}`
    : "";

/** Confirms the settlement before the ID is closed. */
const ConfirmModal = ({ preview, amount, remark, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/id-settlement/settle", { username: preview.member.username, amount: Number(amount), remark });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not settle the ID.");
    toast.success(data?.message || "ID settled.");
    onDone();
  };
  return (
    <Modal show={!!preview} onHide={onClose} centered className="plan-settings-modal">
      <Modal.Header closeButton>
        <Modal.Title>Settle {preview?.member.username}?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {preview && (
          <>
            <p className="mb-2">
              A payout of <strong>{inr(amount)}</strong> is created for <strong>{preview.member.name || preview.member.username}</strong>.
            </p>
            <ul className="ids-confirm-list">
              <li>{preview.orders.length} package{preview.orders.length > 1 ? "s" : ""} ({inr(preview.investment)}) are closed as withdrawn: growth stops.</li>
              <li>The ID gets no more income of any kind.</li>
              <li>{inr(preview.main_wallet)} already in the main wallet stays with the member.</li>
            </ul>
            <p className="ps-muted ps-small mb-0">This cannot be undone.</p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="danger" onClick={submit} disabled={saving}>
          {saving ? <Spinner size="sm" /> : "Settle ID"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Records the bank transfer of a settlement payout. */
const PaidModal = ({ row, onClose, onDone }) => {
  const [utr, setUtr] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setUtr("");
    setRemark("");
  }, [row]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { data, error } = await apiCall("POST", "/id-settlement/mark-paid", { settlement_Id: row.settlement_Id, utr, remark });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not update.");
    toast.success(data?.message || "Marked paid.");
    onDone();
  };
  return (
    <Modal show={!!row} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Mark settlement paid</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {row && (
            <>
              <div className="ar-member-card">
                <span className="ar-avatar">{String(row.name || row.username || "?").slice(0, 2).toUpperCase()}</span>
                <div>
                  <div className="ps-strong">{row.name || row.username}</div>
                  <div className="ps-muted ps-small">{row.username} · settled {moment(row.createdAt).format("DD MMM YYYY")}</div>
                </div>
                <div className="ar-member-amount">
                  <div className="ps-strong">{inr(row.amount)}</div>
                  <div className="ps-muted ps-small">{bankLine(row.account) || "No bank account on file"}</div>
                </div>
              </div>
              <Form.Group className="mt-3">
                <Form.Label>UTR / transaction number</Form.Label>
                <Form.Control value={utr} onChange={(e) => setUtr(e.target.value)} maxLength={100} required autoFocus />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>Note (optional)</Form.Label>
                <Form.Control as="textarea" rows={2} maxLength={300} value={remark} onChange={(e) => setRemark(e.target.value)} />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="success" disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Mark paid"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/** Pay back a member's investment less the income they received, and close the ID. */
const IdSettlement = () => {
  const [username, setUsername] = useState("");
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null);
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [confirm, setConfirm] = useState(false);

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [paying, setPaying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (status !== "") params.set("status", status);
    if (applied) params.set("search", applied);
    const { data, error } = await apiCall("GET", `/id-settlement?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load settlements.");
    setList(data?.data || null);
  }, [page, status, applied]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const calculate = async (e) => {
    e?.preventDefault();
    if (!username.trim()) return;
    setChecking(true);
    const { data, error } = await apiCall("GET", `/id-settlement/preview?username=${encodeURIComponent(username.trim())}`);
    setChecking(false);
    if (error) {
      setPreview(null);
      return toast.error(error.message || "Could not calculate.");
    }
    setPreview(data?.data || null);
    setAmount(String(data?.data?.calculated ?? ""));
    setRemark("");
  };

  const settled = () => {
    setConfirm(false);
    setPreview(null);
    setUsername("");
    setPage(1);
    load();
  };

  const p = preview;
  const amountNum = Number(amount);
  const amountOk = amount !== "" && amountNum >= 0 && p && amountNum <= p.investment;
  const rows = list?.rows || [];
  const total = list?.total || 0;

  return (
    <div className="plan-settings activation-requests-page id-settlement-page">
      <div className="ps-header">
        <div>
          <h2>ID Settlement</h2>
          <p>Pay back a member's investment less the income they already received, and close the ID.</p>
        </div>
        <ExportButtons
          title="ID Settlements"
          filters={[status === "0" ? "status: payout pending" : status === "1" ? "status: paid" : "", applied ? `search: ${applied}` : ""].filter(Boolean).join(", ")}
          columns={[
            { header: "#", value: (r) => r.settlement_Id },
            { header: "Settled on", value: (r) => moment(r.createdAt).format("DD MMM YYYY") },
            { header: "Name", value: (r) => r.name },
            { header: "Username", value: (r) => r.username },
            { header: "Investment", value: (r) => inr(r.investment) },
            { header: "Income received", value: (r) => inr(r.income) },
            { header: "Calculated", value: (r) => inr(r.calculated) },
            { header: "Paid back", value: (r) => inr(r.amount) },
            { header: "Bank", value: (r) => bankLine(r.account) },
            { header: "Status", value: (r) => (r.status === 1 ? "Paid" : "Payout pending") },
            { header: "UTR", value: (r) => r.utr },
            { header: "Paid on", value: (r) => (r.paid_at ? moment(r.paid_at).format("DD MMM YYYY") : "") },
            { header: "Note", value: (r) => r.remark },
          ]}
          getRows={() =>
            collectPages(async (p, l) => {
              const params = new URLSearchParams({ page: p, limit: l });
              if (status !== "") params.set("status", status);
              if (applied) params.set("search", applied);
              const { data } = await apiCall("GET", `/id-settlement?${params.toString()}`);
              return { rows: data?.data?.rows || [], pages: data?.data?.pages || 1 };
            })
          }
        />
      </div>

      <div className="ps-panel ids-calc">
        <Form className="ids-search" onSubmit={calculate}>
          <div>
            <Form.Label>Member username</Form.Label>
            <Form.Control value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. AD1234567" />
          </div>
          <Button type="submit" className="ar-primary-btn" disabled={checking || !username.trim()}>
            {checking ? <Spinner size="sm" /> : "Calculate"}
          </Button>
        </Form>

        {p && (
          <div className="ids-result">
            <div className="ar-member-card">
              <span className="ar-avatar">{String(p.member.name || p.member.username).slice(0, 2).toUpperCase()}</span>
              <div>
                <div className="ps-strong">{p.member.name || p.member.username}</div>
                <div className="ps-muted ps-small">
                  {p.member.username}
                  {p.member.mobile ? ` · ${p.member.mobile}` : ""}
                  {p.member.email ? ` · ${p.member.email}` : ""}
                </div>
              </div>
            </div>

            {p.settled ? (
              <div className="ids-note ids-note--done">
                Already settled on {moment(p.settled.settled_at).format("DD MMM YYYY")} for {inr(p.settled.amount)} (settlement #{p.settled.settlement_Id}).
              </div>
            ) : (
              <>
                <div className="ids-sum">
                  <div className="ids-sum-row">
                    <span>Investment <small>{p.orders.length} package{p.orders.length === 1 ? "" : "s"}</small></span>
                    <strong>{inr(p.investment)}</strong>
                  </div>
                  <div className="ids-sum-row ids-sum-row--minus">
                    <span>Less income received</span>
                    <strong>− {inr(p.income)}</strong>
                  </div>
                  {p.income_breakdown.map((b) => (
                    <div className="ids-sum-row ids-sum-row--sub" key={b.source}>
                      <span>{b.label}</span>
                      <span>{inr(b.amount)}</span>
                    </div>
                  ))}
                  <div className="ids-sum-row ids-sum-row--total">
                    <span>Settlement amount</span>
                    <strong>{inr(p.calculated)}</strong>
                  </div>
                </div>

                <div className="ids-facts">
                  <div>
                    <span>Packages</span>
                    <strong>{p.orders.length ? p.orders.map((o) => `#${o.order_Id} ${inr(o.amount)}`).join(", ") : "None active"}</strong>
                  </div>
                  <div>
                    <span>Main wallet</span>
                    <strong>{inr(p.main_wallet)}</strong>
                    <small>stays with the member</small>
                  </div>
                  <div>
                    <span>Pay to</span>
                    <strong className={p.account ? "" : "ids-warn"}>{bankLine(p.account) || "No bank account added"}</strong>
                  </div>
                </div>

                {p.orders.length ? (
                  <div className="ids-form">
                    <Form.Group>
                      <Form.Label>Amount to pay</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>₹</InputGroup.Text>
                        <Form.Control type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} isInvalid={amount !== "" && !amountOk} />
                        <Form.Control.Feedback type="invalid">Between ₹0 and the investment ({inr(p.investment)}).</Form.Control.Feedback>
                      </InputGroup>
                      {amountOk && amountNum !== p.calculated && <Form.Text>Changed from the calculated {inr(p.calculated)}.</Form.Text>}
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Note (optional)</Form.Label>
                      <Form.Control value={remark} onChange={(e) => setRemark(e.target.value)} maxLength={300} placeholder="e.g. Member asked to close the ID" />
                    </Form.Group>
                    <Button variant="danger" className="ids-settle-btn" disabled={!amountOk} onClick={() => setConfirm(true)}>
                      Settle ID &amp; create payout
                    </Button>
                  </div>
                ) : (
                  <div className="ids-note">This member has no active package, so there is nothing to settle.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat ar-stat--pending">
          <span>Payout pending</span>
          <strong>{inr(list?.summary?.pending?.amount)}</strong>
          <small>{list?.summary?.pending?.count || 0} settlement{list?.summary?.pending?.count === 1 ? "" : "s"}</small>
        </div>
        <div className="ps-stat ar-stat ar-stat--approved">
          <span>Paid</span>
          <strong>{inr(list?.summary?.paid?.amount)}</strong>
          <small>{list?.summary?.paid?.count || 0} settlement{list?.summary?.paid?.count === 1 ? "" : "s"}</small>
        </div>
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Search</Form.Label>
            <Form.Control size="sm" placeholder="Username, name or UTR" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <Form.Label>Status</Form.Label>
            <Form.Select size="sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All</option>
              <option value="0">Payout pending</option>
              <option value="1">Paid</option>
            </Form.Select>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Member</th>
                <th>Investment</th>
                <th>Income received</th>
                <th>Paid back</th>
                <th>Bank</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="ps-empty">{search || status ? "Nothing matches these filters." : "No ID has been settled yet."}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.settlement_Id}>
                    <td className="ps-small">
                      <div className="ps-strong">#{r.settlement_Id}</div>
                      <div className="ps-muted">{moment(r.createdAt).format("DD MMM YYYY")}</div>
                    </td>
                    <td>
                      <div className="ps-strong">{r.name || "—"}</div>
                      <div className="ps-muted ps-small">{r.username}</div>
                    </td>
                    <td>{inr(r.investment)}</td>
                    <td>{inr(r.income)}</td>
                    <td>
                      <div className="ps-strong">{inr(r.amount)}</div>
                      {r.amount !== r.calculated && <div className="ps-muted ps-small">calculated {inr(r.calculated)}</div>}
                    </td>
                    <td className="ps-small">{bankLine(r.account) || <span className="ids-warn">No bank account</span>}</td>
                    <td>
                      <span className={`ids-status ids-status--${r.status === 1 ? "paid" : "pending"}`}>{r.status === 1 ? "Paid" : "Payout pending"}</span>
                      {r.status === 1 && (
                        <div className="ps-muted ps-small">UTR {r.utr} · {moment(r.paid_at).format("DD MMM YYYY")}</div>
                      )}
                      {r.remark && <div className="ps-muted ps-small">“{r.remark}”</div>}
                    </td>
                    <td className="text-end">
                      {r.status !== 1 && (
                        <Button size="sm" variant="success" onClick={() => setPaying(r)}>Mark paid</Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * LIMIT + 1 : 0}–{Math.min(page * LIMIT, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((x) => x - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {list?.pages || 1}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= (list?.pages || 1)} onClick={() => setPage((x) => x + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <ConfirmModal preview={confirm ? p : null} amount={amount} remark={remark} onClose={() => setConfirm(false)} onDone={settled} />
      <PaidModal row={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />
    </div>
  );
};

export default IdSettlement;
